/**
 * Analytics extract — a plain CSV of the filtered rows.
 *
 * This is the "give me the data behind the chart" export, deliberately separate
 * from the bordereau: headers are human-readable rather than Lloyd's field
 * names, and nothing here is validated against a reporting standard.
 */

import { netPremiumOf } from './analytics';
import type { PolicyReportRecord } from './types';
import { toCsv } from './utils';

interface ExportColumn {
  header: string;
  value: (record: PolicyReportRecord) => string | number | null;
}

const COLUMNS: ExportColumn[] = [
  { header: 'Policy Number', value: r => r.policyNumber },
  { header: 'Certificate Reference', value: r => r.certificateReference },
  { header: 'Transaction', value: r => r.transactionType },
  { header: 'Status', value: r => r.status },
  { header: 'Product', value: r => r.product },
  { header: 'Insured', value: r => r.insuredName },
  { header: 'Industry', value: r => r.industry },
  { header: 'Lives Covered', value: r => r.employeeCount },
  { header: 'Risk State', value: r => r.riskState },
  { header: 'Broker Agency', value: r => r.brokerAgency },
  { header: 'Broker Contact', value: r => r.brokerName },
  { header: 'Carrier', value: r => r.carrierName },
  { header: 'Written Date', value: r => r.writtenDate },
  { header: 'Effective Date', value: r => r.effectiveDate },
  { header: 'Expiration Date', value: r => r.expirationDate },
  { header: 'Cancellation Date', value: r => r.cancellationDate },
  { header: 'Gross Written Premium', value: r => r.grossWrittenPremium.toFixed(2) },
  { header: 'Net Premium', value: r => netPremiumOf(r).toFixed(2) },
  { header: 'Taxes and Fees', value: r => r.taxesAndFees.toFixed(2) },
  { header: 'Aggregate Limit', value: r => r.aggregateLimit.toFixed(2) },
  { header: 'Risk Tier', value: r => r.riskTier },
  { header: 'Projected Loss Ratio %', value: r => r.lossRatioPct },
];

export function buildAnalyticsCsv(records: PolicyReportRecord[]): string {
  return toCsv(
    COLUMNS.map(column => column.header),
    records.map(record => COLUMNS.map(column => column.value(record))),
    true
  );
}
